'use client';

import { useIsDesktop } from '@/_hooks/useBreakpoint';
import useCartProduct from '@/_hooks/useCartProduct';
import { IModifierQuantityObj, IRedux } from '@/_store/types';
import { IProductVariant, IProperty, ISavedModifier } from '@/_types';
import { Box, Stack } from '@chakra-ui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useGTM from '@/_hooks/useGTM';
import OriginOptions from '../OriginOptions';
import Modifiers from '../Modifiers';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import { useParams, useRouter } from 'next/navigation';
import useProductDiscount from '@/_hooks/useProductDiscount';
import { useActiveBrand } from '@/_hooks/useActiveBrand';
import { useSafeBackNavigation } from '@/_hooks/useSafeBackNavigation';
import ProductHeader from '../ProductHeader';
import type { IProduct } from '@/entities/product/model/types';
import Favourites from '../Favourites';
import { useQuery } from '@tanstack/react-query';
import productService from '@/_service/product.service';
import Footer from './footer';
import { getBrandHref, resolveBrandId } from '@/_lib/brand-href';

interface IVariantsMap {
  [key: string]: any;
}
interface IProductOption {}

const Details = ({
  data,
  onSuccess,
  onRedirectToCart,
  isModal,
}: {
  data?: IProduct;
  onSuccess?: () => void;
  onRedirectToCart?: () => void;
  isModal?: boolean;
}) => {
  const [startTracking, setStartTracking] = useState(false);
  const [expectedModifier, setExpectedModifier] = useState<string[]>([]);
  const [compulsories, setCompulsories] = useState<ISavedModifier[]>([]);

  const [modifiersPrice, setModifiersPrice] = useState(0);
  const [originProps, setOriginProps] = useState<IProperty[]>([]);
  const [modifiersQuantity, setModifiersQuantity] = useState<
    IModifierQuantityObj[]
  >([]);
  const [productVariants, setProductVariants] = useState<IVariantsMap>({});
  const [orderModifiers, setOrderModifiers] = useState<ISavedModifier[]>([]);
  const [activeVariant, setActiveVariant] = useState<IProductVariant | null>(
    null,
  );
  const [activeOptions, setActiveOptions] = useState<IProductOption[] | null>(
    null,
  );

  const params = useParams();
  const currentLocale = useCustomLocale();
  const scrollToExpectedModifierRef = useRef<HTMLDivElement>(null);
  const { branches } = useSelector((state: IRedux) => state.common);
  const country = useSelector((state: IRedux) => state.settings?.country);
  const isDesktop = useIsDesktop();
  const { sendGTM } = useGTM();

  const router = useRouter();
  const shipperId = useActiveBrand();
  const branch = branches?.[shipperId];

  const { safeBack } = useSafeBackNavigation();
  const productId = activeVariant?.id || data?.id || '';
  const { quantity, onIncrease, onDecrease, productInCart } = useCartProduct(
    productId,
    orderModifiers,
    // { remove: removeFromCartGTM }
  );

  const price: number = useMemo(
    () => activeVariant?.out_price || data?.out_price || 0,
    [activeVariant?.out_price, data?.out_price],
  );

  const { discounts } = useProductDiscount();

  const totalDiscount = useMemo(() => {
    return discounts?.reduce((acc, cur) => {
      if (
        cur?.product_and_discount &&
        Object.keys(cur?.product_and_discount).length > 0
      ) {
        if (cur.product_and_discount[productId]) {
          acc += cur.product_and_discount[productId];
        }
      } else if (cur.discount_for === 'product') {
        if (cur.discount_mode === 'fixed') {
          acc -= cur.discount_amount;
        } else if (cur.discount_mode === 'percent') {
          acc -= (price / 100) * cur.discount_amount;
        }
      }
      return acc;
    }, 0);
  }, [discounts, price, productId]);

  // Get compulsory and not selected modifier
  useEffect(() => {
    if (startTracking && compulsories?.length > 0) {
      let modifierNames: Record<string, string> = {};
      for (const item of compulsories) {
        const hasParent = !!item.parent_id;
        const orderModifier = orderModifiers?.find((element) => {
          const parentMatches = hasParent
            ? element.parent_id === item.parent_id
            : !element.parent_id && element.modifier_id === item.modifier_id;

          const quantityValid = element.modifier_quantity > 0;

          const minAmountValid = hasParent
            ? (modifiersQuantity.find((mod) => mod.id === element.parent_id)
                ?.quantity || 0) >= (element.parent_min_amount || 0)
            : true;

          return parentMatches && quantityValid && minAmountValid;
        });
        if (!orderModifier) {
          let name = hasParent
            ? item?.parent_name?.[currentLocale]
            : item?.name?.[currentLocale];
          if (name) {
            modifierNames[name] = name;
          }
        }
      }

      setExpectedModifier(Object.values(modifierNames));
    }
  }, [compulsories, orderModifiers, startTracking]);

  useEffect(() => {
    scrollToExpectedModifierRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [expectedModifier]);

  useEffect(() => {
    if (!data?.id) return;
    sendGTM('view_item', {
      product: data,
      quantity,
      currency: country?.currency,
    });
  }, [data?.id]);

  const onOptionChange = (val: any, idx: any) => {
    setActiveOptions((prevState: any) =>
      prevState?.map((e: any, i: number) => (i == idx ? val : e)),
    );
  };

  useEffect(() => {
    for (const [key, value] of Object.entries(productVariants)) {
      if (key == activeOptions?.join('_')) {
        setActiveVariant(value);
        break;
      } else {
        setActiveVariant(null);
      }
    }
  }, [activeOptions, productVariants]);
  // Calculate discounts total price

  useEffect(() => {
    if (orderModifiers) {
      let sum = 0;
      for (const modifier of orderModifiers) {
        sum +=
          (modifier.modifier_price || 0) * (modifier.modifier_quantity || 0);
      }
      setModifiersPrice(sum);
    }
  }, [orderModifiers]);

  useEffect(() => {
    if (data && data?.type === 'origin') {
      const variantsMap: { [key: string]: any } = {};
      const optionsMap: { [key: string]: string } = {};
      const firstOptions: any[] = [];
      for (const variantProduct of data?.variant_products) {
        let optionIds = '';
        for (let j = 0; j < variantProduct.product_property.length; j++) {
          const element = variantProduct.product_property[j];
          optionsMap[element.option_id] = element.option_id;
          if (optionIds.length > 0) {
            optionIds += '_' + element.option_id;
          } else {
            optionIds += element.option_id;
          }
        }
        variantsMap[optionIds] = variantProduct;
      }
      setProductVariants(variantsMap);
      setOriginProps(data?.properties);
      Object.keys(variantsMap)[0]
        ?.split('_')
        .forEach((option) => {
          firstOptions.push(option);
        });
      setActiveOptions(firstOptions);
    }
  }, [data]);

  const backToBrand = () => {
    if (onSuccess) {
      onSuccess();
    } else {
      if (isModal) {
        router.push('/');
      } else if (params?.brandSlug || params?.id) {
        router.push(
          getBrandHref(resolveBrandId(String(params.brandSlug || params.id))),
        );
      } else {
        safeBack('/');
      }
    }
  };

  const categoryIds = useMemo(
    () => String(data?.categories?.map((item) => item.id) || ''),
    [data?.categories],
  );

  // Get favourites based on product ID
  const { data: favourites } = useQuery({
    queryKey: ['product-favourites', categoryIds, branch?.menu_id, shipperId],
    queryFn: () =>
      productService
        .getProductFavourites(
          {
            category_ids: categoryIds,
            menu_id: branch?.menu_id,
          },
          shipperId,
        )
        .then((res) => res.data),
    enabled: Boolean(categoryIds && shipperId),
  });

  return (
    <>
      <Stack
        // pr={2}
        // mr={4}
        mt={6}
        pb={{ base: 0, lg: 20 }}
        flex={1}
        overflow={'auto'}
        css={{
          '&::-webkit-scrollbar': {
            width: '3px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#1A202433',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#FFFFFF4D',
          },
        }}
      >
        <ProductHeader data={data} isModal={isModal} />
        <Box flex={1} px={4}>
          <OriginOptions
            originProps={originProps}
            activeOptions={activeOptions}
            productVariants={productVariants}
            onOptionChange={onOptionChange}
          />
          <Modifiers
            productInCart={productInCart}
            product={activeVariant ? activeVariant : data}
            quantity={quantity}
            expectedModifiers={expectedModifier}
            onSaveCompulsories={(e: any[]) => setCompulsories(e)}
            orderModifiers={orderModifiers}
            modifiersQuantity={modifiersQuantity}
            setOrderModifiers={setOrderModifiers}
            setModifiersQuantity={setModifiersQuantity}
            scrollToExpectedModifierRef={scrollToExpectedModifierRef}
          />
        </Box>
        {!isDesktop &&
          favourites?.favourites &&
          favourites?.favourites?.length > 0 && (
            <Box mb={12} px={4}>
              <Favourites data={favourites} />
            </Box>
          )}
      </Stack>
      <Footer
        branch={branch}
        shipperId={shipperId}
        data={data}
        activeVariant={activeVariant}
        totalDiscount={totalDiscount}
        price={price}
        modifiersPrice={modifiersPrice}
        quantity={quantity}
        productInCart={productInCart}
        orderModifiers={orderModifiers}
        modifiersQuantity={modifiersQuantity}
        compulsories={compulsories}
        setStartTracking={setStartTracking}
        onIncrease={onIncrease}
        onDecrease={onDecrease}
        onRedirectToCart={onRedirectToCart}
        backToBrand={backToBrand}
        expectedModifier={expectedModifier}
      />
    </>
  );
};

export default Details;
